import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import edit2Fill from '@iconify/icons-eva/edit-2-fill';
import trash2Fill from '@iconify/icons-eva/trash-2-fill';
import saveFill from '@iconify/icons-eva/save-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import { FormikProvider, useFormik } from 'formik';
import { motion } from 'framer-motion';
import {
  Box,
  Button,
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  TableContainer,
  IconButton,
  TextField,
  Tooltip,
  Checkbox,
  InputAdornment,
  Skeleton
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import Scrollbar from 'src/components/Scrollbar';
import ConfirmationPopup from '../official-request/ConfirmationPopup';
import { REST_API_END_POINT } from 'src/constants/Defaultvalues';
import axios from 'axios';
import moment from 'moment';

const preventInvalidNumberKeys = (event) => {
  if (['-', '+', 'e', 'E'].includes(event.key)) {
    event.preventDefault();
  }
};


const TABLE_HEAD = [
  { id: 'date', label: 'Date', align: 'left' },
  { id: 'description', label: 'Equipment', align: 'left' },
  { id: 'qty', label: 'Hours', align: 'center' },
  { id: 'unitCost', label: 'Cost Rate', align: 'left' },
  { id: 'unitBill', label: 'Bill Rate', align: 'left' },
  { id: 'cost', label: 'Cost', align: 'left' },
  { id: 'bill', label: 'Bill', align: 'left' },
  { id: 'billable', label: 'Billable', align: 'center' },
  { id: 'actions', label: 'Actions', align: 'center' }
];

const toSafeNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const round2 = (value) => Math.round((toSafeNumber(value) + Number.EPSILON) * 100) / 100;
const normalizeBillable = (value) => value === true || value === 1 || value === '1' || value === 'true';

const calculateMargin = (totalBill, totalCost) => {
  const safeTotalBill = toSafeNumber(totalBill);
  const safeTotalCost = toSafeNumber(totalCost);
  if (!safeTotalBill) return safeTotalCost > 0 ? -100 : 0;
  return round2(((safeTotalBill - safeTotalCost) / safeTotalCost) * 100);
};

const calculateRowValues = ({ qty, unitCost, unitBill, billable }) => {
  const safeQty = toSafeNumber(qty);
  const safeUnitCost = toSafeNumber(unitCost);
  const safeUnitBill = toSafeNumber(unitBill);
  const safeBillable = normalizeBillable(billable);
  const cost = safeQty * safeUnitCost;
  // Line bill always shows qty × unit bill; billable only affects summary totals, not this column
  const bill = safeQty * safeUnitBill;
  return {
    qty: round2(safeQty),
    unitCost: round2(safeUnitCost),
    unitBill: round2(safeUnitBill),
    billable: safeBillable,
    cost: round2(cost),
    bill: round2(bill)
  };
};

const formatDate = (unixTs) => {
  return moment.unix(Number(unixTs || 0)).format('DD/MM/YYYY');
};

export default function JobCostOtherTab({
  entries = [],
  setEntries,
  theme,
  isLoading = false,
  onSummaryChange,
  onEquipmentRefresh,
  onJobLogRefresh,
  userId,
  requestId,
  projectId,
  isTM = false,
  entryKind = 'Other'
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [editingUid, setEditingUid] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const mappedRows = useMemo(
    () => (entries || []).map((entry, index) => {
      const calculated = calculateRowValues({
        qty: entry?.quantity ?? 0,
        unitCost: entry?.unit_cost ?? 0,
        unitBill: entry?.unit_bill ?? 0,
        billable: entry?.billable ?? true
      });
      return {
        uid: `other-${entry?.id || entry?.temp_id || index}`,
        id: entry?.id || null,
        dateUnix: entry?.date || Math.floor(Date.now() / 1000),
        date: formatDate(entry?.date || Math.floor(Date.now() / 1000)),
        description: entry?.description || '',
        qty: calculated.qty,
        unitCost: calculated.unitCost,
        unitBill: calculated.unitBill,
        cost: calculated.cost,
        bill: calculated.bill,
        billable: calculated.billable,
        raw: entry
      };
    }),
    [entries]
  );

  useEffect(() => {
    setRows(mappedRows);
  }, [mappedRows]);

  const summaryStats = useMemo(() => {
    const totalCost = rows.reduce((sum, row) => sum + (Number(row.cost) || 0), 0);
    const totalBillable = rows.reduce((sum, row) => sum + (row.billable ? Number(row.bill) || 0 : 0), 0);
    return { totalCost, totalBillable, marginPercent: calculateMargin(totalBillable, totalCost) };
  }, [rows]);

  useEffect(() => {
    if (onSummaryChange) {
      onSummaryChange({
        cost: Number(summaryStats.totalCost || 0),
        billable: Number(summaryStats.totalBillable || 0),
        marginPercent: Number(summaryStats.marginPercent || 0)
      });
    }
  }, [summaryStats, onSummaryChange]);

  // Cleanup unsaved new rows when the component unmounts (e.g., switching tabs)
  useEffect(() => {
    return () => {
      if (setEntries) {
        setEntries((prev) => (prev || []).filter((e) => e.id || !e.__isNew));
      }
    };
  }, [setEntries]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: { description: '', qty: 1, unitCost: 0, unitBill: 0, billable: true },
    onSubmit: async (values) => {
      const row = rows.find((r) => r.uid === editingUid);
      if (!row || !userId || !projectId) return;
      const calculated = calculateRowValues(values);
      try {
        const payload = {
          id: row.id || undefined,
          projectId,
          requestId,
          userId,
          date: row.dateUnix,
          description: values.description,
          quantity: calculated.qty,
          unitCost: calculated.unitCost,
          unitBill: calculated.unitBill,
          billable: calculated.billable ? 1 : 0,
          entryKind
        };
        const res = await axios.post(`${REST_API_END_POINT}project/save-job-cost-other`, payload);
        if (res?.data?.status !== 1) throw new Error(res?.data?.message || 'Failed to save');
        const refreshed = await axios.post(`${REST_API_END_POINT}project/fetch-all-job-cost-others`, { projectId });
        setEntries(refreshed?.data?.data || []);
        if (onEquipmentRefresh) {
          onEquipmentRefresh();
        }
        if (onJobLogRefresh) {
          await onJobLogRefresh();
        }
        setEditingUid(null);
        formik.resetForm({
          values: { description: '', qty: 1, unitCost: 0, unitBill: 0, billable: true }
        });
        enqueueSnackbar('Equipment entry saved', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(error?.message || 'Failed to save equipment entry', { variant: 'error' });
      }
    }
  });

  const handleEditRow = (row) => {
    setEditingUid(row.uid);
    formik.setValues({
      description: row.description,
      qty: round2(row.qty),
      unitCost: row.unitCost,
      unitBill: row.unitBill,
      billable: row.billable
    });
  };

  const handleAddRow = () => {
    const now = Math.floor(Date.now() / 1000);
    const tempId = `tmp-${Date.now()}`;
    setEntries((prev) => ([
      {
        temp_id: tempId,
        id: null,
        project_id: projectId,
        request_id: requestId || null,
        date: now,
        description: '',
        quantity: 1,
        unit_cost: 0,
        unit_bill: 0,
        total_cost: 0,
        total_bill: 0,
        billable: 1,
        status: 1,
        taken: 1,
        timestamp: now,
        __isNew: true
      },
      ...(prev || [])
    ]));
    setEditingUid(`other-${tempId}`);
    formik.resetForm({
      values: { description: '', qty: 1, unitCost: 0, unitBill: 0, billable: true }
    });
  };

  const handleCloseEdit = () => {
    const row = rows.find((r) => r.uid === editingUid);
    if (row && !row.id) {
      setEntries((prev) => (prev || []).filter((entry, idx) => {
        const uid = `other-${entry?.id || entry?.temp_id || idx}`;
        return uid !== row.uid;
      }));
    }
    setEditingUid(null);
    formik.resetForm({
      values: { description: '', qty: 1, unitCost: 0, unitBill: 0, billable: true }
    });
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      if (itemToDelete?.id) {
        await axios.post(`${REST_API_END_POINT}project/delete-job-cost-other`, {
          id: itemToDelete.id,
          userId,
          entryKind
        });
      }
      setEntries((prev) => (prev || []).filter((entry, idx) => {
        const uid = `other-${entry?.id || entry?.temp_id || idx}`;
        return uid !== itemToDelete.uid;
      }));
      if (onEquipmentRefresh) {
        onEquipmentRefresh();
      }
      if (onJobLogRefresh) {
        await onJobLogRefresh();
      }
      enqueueSnackbar('Equipment entry removed', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Failed to remove equipment entry', { variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  return (
    <Box sx={{ mt: 3, pl: 2, pr: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }} alignItems={{ xs: 'flex-start', md: 'center' }}>
        <Tooltip title={`Total Cost = SUM(Row Cost) = ${summaryStats.totalCost.toFixed(2)}`} arrow>
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'transparent', px: 2, py: 1, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Box component={'span'} className='mdi--dollar' width={18} height={18} sx={{ mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary' }}>
              Cost: ${summaryStats.totalCost.toFixed(2)}
            </Typography>
          </Box>
        </Tooltip>
        {/* {!isTM && (
          <Tooltip title={`Billable = SUM(Row Bill where Billable is checked) = ${summaryStats.totalBillable.toFixed(2)}`} arrow>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: 'primary.main', px: 2, py: 1, borderRadius: 1 }}>
              <Box component={'span'} className='mdi--dollar' width={18} height={18} sx={{ mr: 0.5, color: 'common.white' }} />
              <Typography variant="subtitle2" sx={{ color: 'common.white', fontWeight: 800 }}>
                Billable: ${summaryStats.totalBillable.toFixed(2)}
              </Typography>
            </Box>
          </Tooltip>
        )}
        {!isTM && (
          <Tooltip
            title={`Margin % = ((Billable - Cost) / Cost) x 100 = (($${summaryStats.totalBillable.toFixed(2)} - $${summaryStats.totalCost.toFixed(2)}) / $${summaryStats.totalCost.toFixed(2)}) x 100 = ${summaryStats.marginPercent.toFixed(2)}%`}
            arrow
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                bgcolor: (t) =>
                  summaryStats.marginPercent < 0
                    ? (t.palette.mode === 'dark' ? `${t.palette.error.main}1a` : '#ffebee')
                    : (t.palette.mode === 'dark' ? `${t.palette.success.main}1a` : '#e8f5e9'),
                px: 2,
                py: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: summaryStats.marginPercent < 0 ? 'error.light' : 'success.light'
              }}
            >
              <Box
                component={'span'}
                className={summaryStats.marginPercent < 0 ? 'ph--trend-down' : 'ph--trend-up'}
                width={18}
                height={18}
                sx={{ mr: 1, color: summaryStats.marginPercent < 0 ? 'error.main' : '#54D62C' }}
              />
              <Typography variant="subtitle2" sx={{ color: summaryStats.marginPercent < 0 ? 'error.dark' : 'success.dark', fontWeight: 800 }}>
                Margin: {summaryStats.marginPercent.toFixed(2)}%
              </Typography>
            </Box>
          </Tooltip>
        )} */}
      </Stack>

      <Card sx={{ boxShadow: 'none', border: `1px solid ${theme.palette.divider}`, borderRadius: 1.5, overflow: 'hidden' }}>
        <Scrollbar>
          <TableContainer sx={{ minWidth: 700 }}>
            <Table size="small">
              <TableBody>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  {TABLE_HEAD.map((head) => {
                    if (['unitBill', 'bill', 'billable'].includes(head.id)) return null;
                    return (
                      <TableCell key={head.id} align={head.align} sx={{ fontWeight: 800, color: 'text.secondary', py: 1.8, fontSize: '0.8rem', textTransform: 'uppercase' }}>
                        {head.label}
                      </TableCell>
                    );
                  })}
                  {/* {TABLE_HEAD.filter(h => !isTM || !['unitBill', 'bill', 'billable'].includes(h.id)).map((head) => (
                    <TableCell key={head.id} align={head.align} sx={{ fontWeight: 800, color: 'text.secondary', py: 1.8, fontSize: '0.8rem', textTransform: 'uppercase' }}>
                      {head.label}
                    </TableCell>
                  ))} */}
                </TableRow>
                {isLoading ? (
                  [...Array(4)].map((_, i) => (
                    <TableRow key={i}>
                      {/* {[...Array(TABLE_HEAD.filter(h => !isTM || !['unitBill', 'bill', 'billable'].includes(h.id)).length)].map((__, j) => ( */}
                      {[...Array(TABLE_HEAD.filter(h => !['unitBill', 'bill', 'billable'].includes(h.id)).length)].map((__, j) => (
                        <TableCell key={j}><Skeleton variant="text" width="60%" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : rows.map((row, index) => {
                  const isEditing = editingUid === row.uid;
                  const fv = formik.values;
                  const editCalculated = isEditing ? calculateRowValues(fv) : null;
                  return (
                    <TableRow
                      key={row.uid}
                      component={motion.tr}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      sx={{ borderBottom: (t) => `solid 1px ${t.palette.divider}`, '&:hover': { bgcolor: 'action.hover' } }}
                    >
                      <TableCell>{row.date}</TableCell>
                      {isEditing ? (
                        <FormikProvider value={formik}>
                          <>
                            <TableCell>
                              {isTM && row.id ? (
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {fv.description}
                                </Typography>
                              ) : (
                                <TextField size="small" fullWidth value={fv.description} onChange={(e) => formik.setFieldValue('description', e.target.value)} />
                              )}
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                value={fv.qty}
                                onKeyDown={preventInvalidNumberKeys}
                                inputProps={{ min: 0, step: 1 }}
                                onChange={(e) => formik.setFieldValue('qty', e.target.value)}
                                sx={{ width: 80, mx: 'auto' }}
                              />
                            </TableCell>
                             <TableCell><TextField size="small" type="number" value={fv.unitCost} onKeyDown={preventInvalidNumberKeys} inputProps={{ min: 0 }} onChange={(e) => formik.setFieldValue('unitCost', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ width: 110 }} /></TableCell>
                            {/* {!isTM && <TableCell><TextField size="small" type="number" value={fv.unitBill} onKeyDown={preventInvalidNumberKeys} inputProps={{ min: 0 }} onChange={(e) => formik.setFieldValue('unitBill', e.target.value)} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} sx={{ width: 110 }} /></TableCell>} */}
                            <TableCell>
                              <Tooltip title={`Cost = Hours x Cost Rate = ${Number(editCalculated.qty || 0).toFixed(2)} x ${Number(editCalculated.unitCost || 0).toFixed(2)} = ${editCalculated.cost.toFixed(2)}`} arrow>
                                <Typography component="span">${editCalculated.cost.toFixed(2)}</Typography>
                              </Tooltip>
                            </TableCell>
                            {/* {!isTM && (
                              <TableCell sx={{ color: 'primary.main', fontWeight: 700 }}>
                                <Tooltip title={`Bill = Hours x Bill Rate = ${Number(editCalculated.qty || 0).toFixed(2)} x ${Number(editCalculated.unitBill || 0).toFixed(2)} = ${editCalculated.bill.toFixed(2)}`} arrow>
                                  <Typography component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>${editCalculated.bill.toFixed(2)}</Typography>
                                </Tooltip>
                              </TableCell>
                            )}
                            {!isTM && <TableCell align="center"><Checkbox checked={Boolean(fv.billable)} onChange={(e) => formik.setFieldValue('billable', e.target.checked)} /></TableCell>} */}
                            <TableCell align="center">
                              <Stack direction="row" spacing={1} justifyContent="center">
                                <IconButton size="small" color="primary" onClick={formik.handleSubmit}><Icon icon={saveFill} width={18} height={18} /></IconButton>
                                <IconButton size="small" color="inherit" onClick={handleCloseEdit}><Icon icon={closeFill} width={18} height={18} /></IconButton>
                              </Stack>
                            </TableCell>
                          </>
                        </FormikProvider>
                      ) : (
                        <>
                          <TableCell><Typography variant="subtitle2" sx={{ fontWeight: 600, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.description}</Typography></TableCell>
                          <TableCell align="center">{Number(row.qty || 0).toFixed(2)}</TableCell>
                          <TableCell>${Number(row.unitCost || 0).toFixed(2)}</TableCell>
                          {/* {!isTM && <TableCell>${Number(row.unitBill || 0).toFixed(2)}</TableCell>} */}
                          <TableCell>
                            <Tooltip title={`Cost = Hours x Cost Rate = ${Number(row.qty || 0).toFixed(2)} x ${Number(row.unitCost || 0).toFixed(2)} = ${Number(row.cost || 0).toFixed(2)}`} arrow>
                              <Typography variant="subtitle2">${Number(row.cost || 0).toFixed(2)}</Typography>
                            </Tooltip>
                          </TableCell>
                          {/* {!isTM && (
                            <TableCell sx={{ fontWeight: 700, color: 'primary.main' }}>
                              <Tooltip title={`Bill = Hours x Bill Rate = ${Number(row.qty || 0).toFixed(2)} x ${Number(row.unitBill || 0).toFixed(2)} = ${Number(row.bill || 0).toFixed(2)}`} arrow>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>${Number(row.bill || 0).toFixed(2)}</Typography>
                              </Tooltip>
                            </TableCell>
                          )}
                          {!isTM && (
                            <TableCell align="center">
                              <Box sx={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: row.billable ? 'success.main' : 'error.main', mx: 'auto' }}>
                                <Icon icon={row.billable ? checkmarkCircle2Fill : closeCircleFill} width={14} height={14} style={{ color: 'white' }} />
                              </Box>
                            </TableCell>
                          )} */}
                          <TableCell align="center">
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <IconButton size="small" onClick={() => handleEditRow(row)}><Icon icon={edit2Fill} width={18} height={18} /></IconButton>
                              <IconButton size="small" color="error" onClick={() => { setItemToDelete(row); setDeleteDialogOpen(true); }}><Icon icon={trash2Fill} width={18} height={18} /></IconButton>
                            </Stack>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>
      <Button
        variant="text"
        onClick={handleAddRow}
        startIcon={<Box component="span" sx={{ fontSize: 20, lineHeight: 1, fontWeight: 500 }}>+</Box>}
        sx={{ mt: 2, mb: 2, pl: 1, color: 'primary.main', fontWeight: 700 }}
      >
        Add Equipment Entry
      </Button>

      <ConfirmationPopup
        open={deleteDialogOpen}
        handleClose={() => setDeleteDialogOpen(false)}
        message="Are you sure you want to remove this entry?"
        handleClick={handleDeleteConfirm}
      />
    </Box>
  );
}

